import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Chip,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { useLanguage } from '../../../utils/i18n';

export default function VehicleStatusDialog({ open, onClose, title, data }) {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeFilter, setActiveFilter] = useState(null);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setPage(0);
      setActiveFilter(null);
    }
  }, [open]);

  const handleFilterClick = (condition) => {
    setActiveFilter((prev) => (prev === condition ? null : condition));
    setPage(0);
  };

  const { processedData, summary } = useMemo(() => {
    let all = 0,
      onTrip = 0,
      off = 0,
      idle = 0,
      maintenance = 0,
      parking = 0,
      delay = 0;

    const formattedRows = (data || []).map((item, index) => {
      const v = item.raw || item;
      let condition = 'Unknown';
      let colorChip = 'default';

      const statusRaw = String(v.status || v.position || v.location || '').toLowerCase();
      const engineRaw = String(v.engine || '').toUpperCase();
      const speed = Number(v.speed || 0);

      const isDelay = statusRaw.includes('delay');

      if (isDelay) {
        condition = 'Delay';
        colorChip = 'warning';
        delay++;
      } else if (statusRaw.includes('maintenace') || statusRaw.includes('maintenance')) {
        condition = 'Maintenance';
        colorChip = 'error';
        maintenance++;
      } else if (statusRaw.includes('pool') || statusRaw.includes('customer') || statusRaw.includes('geofence')) {
        condition = 'Parking';
        colorChip = 'secondary';
        parking++;
      } else if (statusRaw.includes('trip') || (engineRaw === 'ON' && speed > 0)) {
        condition = 'On trip';
        colorChip = 'success';
        onTrip++;
      } else if (engineRaw === 'OFF') {
        condition = 'OFF';
        colorChip = 'default';
        off++;
      } else if (engineRaw === 'IDLE' || (engineRaw === 'ON' && speed === 0)) {
        condition = 'IDLE';
        colorChip = 'warning';
        idle++;
      }

      all++;

      return {
        id: v.id || v.vehicle_id || `v-${index}`,
        license_plate: v.vehicle_license_plate || v.license_plate || '-',
        condition,
        colorChip,
        lastUpdate: v.last_update ? dayjs(v.last_update).format('YYYY-MM-DD HH:mm:ss') : '-',
        engine: engineRaw || '-',
        speed: speed !== undefined ? `${speed} Km/H` : '-',
        location: v.location || v.street || '-'
      };
    });

    return { processedData: formattedRows, summary: { all, onTrip, off, idle, maintenance, parking, delay } };
  }, [data]);

  const filteredRows = useMemo(() => {
    let result = processedData;

    if (activeFilter) {
      result = result.filter((row) => row.condition === activeFilter);
    }

    if (!searchTerm) return result;
    const lower = searchTerm.toLowerCase();
    return result.filter(
      (r) =>
        r.license_plate.toLowerCase().includes(lower) ||
        r.condition.toLowerCase().includes(lower) ||
        r.location.toLowerCase().includes(lower)
    );
  }, [processedData, searchTerm, activeFilter]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const headerStyle = {
    backgroundColor: '#485D9A',
    color: 'white',
    border: '1px solid #ddd',
    padding: '12px 8px',
    textAlign: 'left',
    fontWeight: 'bold'
  };
  const cellStyle = { border: '1px solid #ddd', padding: '8px', fontSize: '0.8rem' };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        {title}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 2, bgcolor: '#f4f6f8' }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip
            label={`${t('dashboardAsset.all', 'All')}: ${summary.all}`}
            size="small"
            onClick={() => handleFilterClick(null)}
            sx={{
              bgcolor: '#e3f2fd',
              color: '#1976d2',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter !== null ? 0.5 : 1,
              border: activeFilter === null ? '1px solid #1976d2' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1976d2', ml: 1 }} />}
          />
          <Chip
            label={`${t('dashboardAsset.onTrip', 'On trip')}: ${summary.onTrip}`}
            size="small"
            onClick={() => handleFilterClick('On trip')}
            sx={{
              bgcolor: '#e8f5e9',
              color: '#2e7d32',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter && activeFilter !== 'On trip' ? 0.5 : 1,
              border: activeFilter === 'On trip' ? '1px solid #2e7d32' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2e7d32', ml: 1 }} />}
          />
          <Chip
            label={`${t('dashboardAsset.off', 'OFF')}: ${summary.off}`}
            size="small"
            onClick={() => handleFilterClick('OFF')}
            sx={{
              bgcolor: '#f5f5f5',
              color: '#424242',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter && activeFilter !== 'OFF' ? 0.5 : 1,
              border: activeFilter === 'OFF' ? '1px solid #424242' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#424242', ml: 1 }} />}
          />
          <Chip
            label={`${t('dashboardAsset.idle', 'IDLE')}: ${summary.idle}`}
            size="small"
            onClick={() => handleFilterClick('IDLE')}
            sx={{
              bgcolor: '#fff8e1',
              color: '#fbc02d',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter && activeFilter !== 'IDLE' ? 0.5 : 1,
              border: activeFilter === 'IDLE' ? '1px solid #fbc02d' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fbc02d', ml: 1 }} />}
          />
          <Chip
            label={`${t('dashboardAsset.maintenance', 'Maintenance')}: ${summary.maintenance}`}
            size="small"
            onClick={() => handleFilterClick('Maintenance')}
            sx={{
              bgcolor: '#ffebee',
              color: '#d32f2f',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter && activeFilter !== 'Maintenance' ? 0.5 : 1,
              border: activeFilter === 'Maintenance' ? '1px solid #d32f2f' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#d32f2f', ml: 1 }} />}
          />
          <Chip
            label={`${t('dashboardAsset.parking', 'Parking')}: ${summary.parking}`}
            size="small"
            onClick={() => handleFilterClick('Parking')}
            sx={{
              bgcolor: '#f3e5f5',
              color: '#7b1fa2',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter && activeFilter !== 'Parking' ? 0.5 : 1,
              border: activeFilter === 'Parking' ? '1px solid #7b1fa2' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#7b1fa2', ml: 1 }} />}
          />
          <Chip
            label={`${t('dashboardAsset.delay', 'Delay')}: ${summary.delay}`}
            size="small"
            onClick={() => handleFilterClick('Delay')}
            sx={{
              bgcolor: '#fff3e0',
              color: '#f57c00',
              fontWeight: 'bold',
              cursor: 'pointer',
              opacity: activeFilter && activeFilter !== 'Delay' ? 0.5 : 1,
              border: activeFilter === 'Delay' ? '1px solid #f57c00' : 'none'
            }}
            icon={<Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f57c00', ml: 1 }} />}
          />
        </Stack>

        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder={t('dashboardAsset.search', 'Search...')}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Box>
        <Box sx={{ overflowX: 'auto', border: '1px solid #ddd', borderRadius: 1, bgcolor: '#ffffff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr>
                <th style={headerStyle}>{t('dashboardAsset.licensePlate', 'License Plate')}</th>
                <th style={headerStyle}>{t('dashboardAsset.condition', 'Condition')}</th>
                <th style={headerStyle}>{t('dashboardAsset.lastUpdate', 'Last Update')}</th>
                <th style={headerStyle}>{t('dashboardAsset.engine', 'Engine')}</th>
                <th style={headerStyle}>{t('dashboardAsset.speed', 'Speed')}</th>
                <th style={headerStyle}>{t('dashboardAsset.location', 'Location')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ ...cellStyle, textAlign: 'center' }}>
                    {t('dashboardAsset.noDataFound', 'No data found')}
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row) => (
                  <tr key={row.id}>
                    <td style={cellStyle}>{row.license_plate}</td>
                    <td style={cellStyle}>
                      <Chip label={row.condition} color={row.colorChip} size="small" sx={{ fontWeight: 'bold' }} variant="outlined" />
                    </td>
                    <td style={cellStyle}>{row.lastUpdate}</td>
                    <td style={cellStyle}>{row.engine}</td>
                    <td style={cellStyle}>{row.speed}</td>
                    <td style={cellStyle}>{row.location}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
